import React, { useState, useEffect, useMemo } from 'react';

const MONTH_NAMES = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
};
const DAY_NAMES = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ar: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
};

const toYMD = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const isSameOrBefore = (a, b) => new Date(a).getTime() <= new Date(b).getTime();
const isBetween = (date, start, end) => {
  const d = new Date(date).getTime();
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  return d > s && d < e;
};

function getPackageRanges(packages) {
  const ranges = new Map();
  (packages || []).forEach((pkg) => {
    if (!pkg.isActive || !pkg.selectedDates?.length) return;
    const id = pkg._id || pkg.id || Symbol('pkg');
    const sorted = [...pkg.selectedDates]
      .map((d) => toYMD(new Date(d)))
      .sort();
    const list = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const curr = sorted[i];
      const prevDate = new Date(prev);
      const currDate = new Date(curr);
      const diffDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);
      if (diffDays > 1) {
        list.push({ start, end: prev });
        start = curr;
      }
      prev = curr;
    }
    list.push({ start, end: prev });
    ranges.set(id, list);
  });
  return ranges;
}

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

/** Normalize to YYYY-MM-DD (local date only) to avoid timezone bugs */
function toYMDSafe(d) {
  if (d == null) return '';
  const x = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
  if (!(x instanceof Date) || Number.isNaN(x.getTime())) return '';
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Stay nights = checkIn inclusive, checkOut exclusive. Returns Set of YMD strings. */
function getStayNightYMDs(checkIn, checkOut) {
  const set = new Set();
  const start = new Date(typeof checkIn === 'string' && checkIn.match(/^\d{4}-\d{2}-\d{2}$/) ? checkIn + 'T12:00:00' : checkIn);
  const end = new Date(typeof checkOut === 'string' && checkOut.match(/^\d{4}-\d{2}-\d{2}$/) ? checkOut + 'T12:00:00' : checkOut);
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    set.add(toYMDSafe(d));
  }
  return set;
}

/** Package nights = from first date to last date EXCLUSIVE (last date is check-out day). So package "1 to 6" = nights 1,2,3,4,5; book check-in 1, check-out 6. */
function getPackageNightYMDs(pkg) {
  const dates = pkg?.selectedDates;
  if (!Array.isArray(dates) || dates.length === 0) return new Set();
  const ymds = dates
    .map((d) => toYMDSafe(typeof d === 'string' || typeof d === 'number' ? new Date(d) : d))
    .filter(Boolean)
    .sort();
  if (ymds.length === 0) return new Set();
  const set = new Set();
  const start = new Date(ymds[0] + 'T12:00:00');
  const end = new Date(ymds[ymds.length - 1] + 'T12:00:00');
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    set.add(toYMDSafe(d));
  }
  return set;
}

/**
 * Returns true if the stay is a partial package (overlaps a package but does not include all package nights).
 * User can only book (1) full package or (2) no package overlap.
 */
export function hasPartialPackageSelection(packages, checkIn, checkOut) {
  if (!checkIn || !checkOut || !packages?.length) return false;
  const stayNights = getStayNightYMDs(checkIn, checkOut);
  for (const pkg of packages) {
    if (pkg && pkg.isActive === false) continue;
    const pkgNights = getPackageNightYMDs(pkg);
    if (pkgNights.size === 0) continue;
    const overlap = [...pkgNights].filter((ymd) => stayNights.has(ymd));
    if (overlap.length === 0) continue;
    if (overlap.length < pkgNights.size) return true;
  }
  return false;
}

/**
 * Inline booking calendar: check-in then check-out.
 * Clean, minimal design with clear states for booked, package, and selected dates.
 */
const BookingCalendar = ({
  checkIn = '',
  checkOut = '',
  onChange,
  onPartialPackageAttempt,
  blockedDates = [],
  packages = [],
  language = 'en',
  monthsAhead = 12,
  minNights = 1,
  compact = false
}) => {
  const lang = language?.slice(0, 2) === 'ar' ? 'ar' : 'en';
  const blockedSet = useMemo(() => new Set(blockedDates || []), [blockedDates]);
  // All days between start and end of each package (inclusive) – highlighted in green. e.g. package 1 Feb–5 Feb → 1,2,3,4,5 green.
  const packageDateSet = useMemo(() => {
    const set = new Set();
    (packages || []).forEach((pkg) => {
      if (pkg && pkg.isActive === false) return;
      const dates = pkg?.selectedDates;
      if (!Array.isArray(dates) || dates.length === 0) return;
      const sorted = [...dates]
        .map((d) => (d == null ? null : toYMD(typeof d === 'string' || typeof d === 'number' ? new Date(d) : d)))
        .filter(Boolean)
        .sort();
      if (sorted.length === 0) return;
      const startYMD = sorted[0];
      const endYMD = sorted[sorted.length - 1];
      const start = new Date(startYMD + 'T12:00:00');
      const end = new Date(endYMD + 'T12:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        set.add(toYMD(d));
      }
    });
    return set;
  }, [packages]);
  const today = useMemo(() => toYMD(new Date()), []);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selecting, setSelecting] = useState('checkIn');

  useEffect(() => {
    if (checkIn && checkOut) setSelecting('checkIn');
  }, [checkIn, checkOut]);

  const startMonth = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthOffset);
    return d;
  }, [monthOffset]);

  const monthGrid = (offset) => {
    const d = new Date(startMonth);
    d.setMonth(d.getMonth() + offset);
    const year = d.getFullYear();
    const month = d.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const totalCells = Math.ceil((startPad + daysInMonth) / 7) * 7;
    const cells = [];
    for (let i = 0; i < startPad; i++) {
      const prevMonth = new Date(year, month, -startPad + i + 1);
      cells.push({ date: prevMonth, currentMonth: false, ymd: toYMD(prevMonth) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      cells.push({ date, currentMonth: true, ymd: toYMD(date) });
    }
    const filled = cells.length;
    for (let i = filled; i < totalCells; i++) {
      const nextMonth = new Date(year, month + 1, i - filled + 1);
      cells.push({ date: nextMonth, currentMonth: false, ymd: toYMD(nextMonth) });
    }
    return { year, month, cells };
  };

  const isDisabled = (ymd) => blockedSet.has(ymd) || ymd < today;

  const handleDateClick = (ymd, currentMonth) => {
    if (!currentMonth || isDisabled(ymd)) return;
    if (selecting === 'checkIn') {
      if (checkOut && ymd >= checkOut) {
        onChange(ymd, '');
        setSelecting('checkOut');
        return;
      }
      onChange(ymd, checkOut && isSameOrBefore(checkOut, ymd) ? '' : checkOut);
      setSelecting('checkOut');
    } else {
      if (ymd <= checkIn) {
        onChange(ymd, '');
        setSelecting('checkOut');
        return;
      }
      const nights = Math.ceil((new Date(ymd) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
      if (nights < minNights) return;
      // Prevent partial package: do not allow check-out that would create a partial package
      if (hasPartialPackageSelection(packages, checkIn, ymd)) {
        if (onPartialPackageAttempt) onPartialPackageAttempt();
        return;
      }
      onChange(checkIn, ymd);
      setSelecting('checkIn');
    }
  };

  const monthLabel = (offset) => {
    const d = new Date(startMonth);
    d.setMonth(d.getMonth() + offset);
    const names = MONTH_NAMES[lang] || MONTH_NAMES.en;
    return `${names[d.getMonth()]} ${d.getFullYear()}`;
  };

  const dayNames = DAY_NAMES[lang] || DAY_NAMES.en;
  const cellPx = compact ? 32 : 36;

  const twoMonthWidth = 2 * (7 * cellPx + 6 * 4) + 24;
  return (
    <div
      className="booking-calendar rounded-2xl overflow-visible"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.12)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        minWidth: `${twoMonthWidth}px`
      }}
    >
      {/* Header: navigation + month labels */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-slate-700/50">
        <button
          type="button"
          onClick={() => setMonthOffset(Math.max(-1, monthOffset - 1))}
          disabled={monthOffset <= -1}
          className="flex items-center justify-center w-9 h-9 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-600/50 hover:border-slate-500 hover:text-white disabled:opacity-40 disabled:pointer-events-none transition-colors"
          aria-label="Previous month"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-slate-200 font-medium text-sm tracking-tight">
          {monthLabel(0)} — {monthLabel(1)}
        </span>
        <button
          type="button"
          onClick={() => setMonthOffset(Math.min(monthsAhead - 2, monthOffset + 1))}
          disabled={monthOffset >= monthsAhead - 2}
          className="flex items-center justify-center w-9 h-9 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-600/50 hover:border-slate-500 hover:text-white disabled:opacity-40 disabled:pointer-events-none transition-colors"
          aria-label="Next month"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Month grids - explicit 7-column grid so it never stacks vertically */}
      <div className="p-4 pb-3">
        <div
          className="flex flex-nowrap gap-6"
          style={{ minWidth: `${twoMonthWidth}px` }}
        >
          {[0, 1].map((offset) => {
            const { year, month, cells } = monthGrid(offset);
            const names = MONTH_NAMES[lang] || MONTH_NAMES.en;
            const title = `${names[month]} ${year}`;
            const colWidth = cellPx;
            const gap = 4;
            const monthWidth = 7 * colWidth + 6 * gap;
            return (
              <div key={offset} style={{ width: monthWidth, flexShrink: 0 }}>
                <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2 px-0.5">
                  {title}
                </div>
                {/* Day names row - explicit 7 columns (normal calendar layout) */}
                <div
                  className="mb-1.5"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(7, ${colWidth}px)`,
                    gap: `${gap}px`
                  }}
                >
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className="flex items-center justify-center py-1 text-slate-500 text-[10px] font-medium"
                    >
                      {day}
                    </div>
                  ))}
                </div>
                {/* Date cells - explicit 7 columns, 6 rows */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(7, ${colWidth}px)`,
                    gridAutoRows: `${colWidth}px`,
                    gap: `${gap}px`
                  }}
                >
                  {cells.map(({ date, currentMonth, ymd }) => {
                    const disabled = isDisabled(ymd);
                    const isCheckIn = ymd === checkIn;
                    const isCheckOut = ymd === checkOut;
                    const inRange = checkIn && checkOut && isBetween(ymd, checkIn, checkOut);
                    const isPackage = packageDateSet.has(ymd);
                    const isBooked = blockedSet.has(ymd);
                    const isToday = ymd === today;

                    let cellStyle = '';
                    if (!currentMonth) {
                      cellStyle = 'opacity-30 text-slate-500 pointer-events-none';
                    } else if (disabled) {
                      cellStyle = 'bg-slate-700/50 text-slate-500 cursor-not-allowed rounded-full';
                      if (isBooked) cellStyle += ' bg-red-950/40 text-red-300/90';
                    } else {
                      cellStyle = 'text-slate-200 hover:bg-slate-600/70 cursor-pointer';
                      if (isToday && !isCheckIn && !isCheckOut && !inRange)
                        cellStyle += ' ring-1 ring-slate-400 ring-inset';
                      if (isPackage && !isCheckIn && !isCheckOut && !inRange)
                        cellStyle += ' bg-emerald-950/30'; // package day tint (green)
                      if (inRange) cellStyle += ' bg-[#D5B577]/30 text-white';
                      if (isCheckIn || isCheckOut)
                        cellStyle = 'bg-[#D5B577] text-[#0f172a] font-semibold cursor-pointer shadow-sm';
                    }

                    return (
                      <button
                        key={ymd}
                        type="button"
                        onClick={() => handleDateClick(ymd, currentMonth)}
                        disabled={disabled}
                        title={
                          isBooked
                            ? lang === 'ar'
                              ? 'محجوز'
                              : 'Booked'
                            : isPackage
                              ? lang === 'ar'
                                ? 'يوم باقة'
                                : 'Package day'
                              : undefined
                        }
                        className={`
                          w-full h-full rounded-full font-medium transition-all duration-150 flex items-center justify-center text-sm relative
                          ${cellStyle}
                        `}
                        style={{
                          minWidth: cellPx,
                          minHeight: cellPx,
                          width: cellPx,
                          height: cellPx
                        }}
                      >
                        {isPackage && !isCheckIn && !isCheckOut && !inRange && !disabled ? (
                          <span
                            className="rounded-full border-2 flex items-center justify-center font-semibold text-xs text-white flex-shrink-0"
                            style={{
                              minWidth: Math.min(26, cellPx - 4),
                              minHeight: Math.min(26, cellPx - 4),
                              width: Math.min(26, cellPx - 4),
                              height: Math.min(26, cellPx - 4),
                              borderColor: 'rgb(52 211 153)',
                              backgroundColor: 'rgba(16, 185, 129, 0.6)',
                              boxShadow: '0 0 0 1px rgba(52,211,153,0.3)'
                            }}
                          >
                            {date.getDate()}
                          </span>
                        ) : (
                          <span className="rounded-full w-full h-full flex items-center justify-center">
                            {date.getDate()}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-t border-slate-700/50 flex flex-wrap items-center gap-4 text-[11px] text-slate-400">
        <span className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm bg-[#D5B577]" />
          {lang === 'ar' ? 'محدد' : 'Selected'}
        </span>
        {(packages?.length > 0 || packageDateSet.size > 0) && (
          <span className="flex items-center gap-2">
            <span
              className="rounded-full border-2 flex-shrink-0"
              style={{ width: 10, height: 10, borderColor: 'rgb(52 211 153)', backgroundColor: 'rgba(16, 185, 129, 0.6)' }}
            />
            {lang === 'ar' ? 'يوم باقة' : 'Package day'}
          </span>
        )}
        <span className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-950/40 border border-red-400/30" />
          {lang === 'ar' ? 'محجوز' : 'Booked'}
        </span>
      </div>
    </div>
  );
};

export default BookingCalendar;
